import type {
  ProductImageArea,
  ProductImageEntry,
  ProductImageText,
} from "./types";

/**
 * Settings, account, integration and communication surfaces from the 2026-10
 * surface campaign, captured in S5. Dialogs and drawers are opened and closed
 * without saving; one-time secrets are masked before the shutter.
 */
type Draft = {
  id: string;
  module: string;
  opens: string;
  setup: readonly string[];
  sv: ProductImageText;
  en: ProductImageText;
  area?: ProductImageArea;
};

const NOT_SAVED_SV = "Inget sparas när dialogen stängs.";
const NOT_SAVED_EN = "Nothing is saved when the dialog is closed.";
const MASKED_SV = "Hemligheten är dold i bilden.";
const MASKED_EN = "The secret is hidden in the image.";

function entry(draft: Draft): ProductImageEntry {
  return {
    id: draft.id,
    module: draft.module,
    area: draft.area ?? "standing-data",
    opens: draft.opens,
    setup: draft.setup,
    scenario: "S5",
    visual: { variant: 0 },
    text: { sv: draft.sv, en: draft.en },
    use: ["help"],
  };
}

const INSETS = "Inställningar → Insatser";
const SKILLS = "Inställningar → Kompetenser & taggar";
const ROLES = "Inställningar → Medarbetarroller";
const INTEGRATIONS = "Inställningar → Integrationer";
const ACCOUNT = "Konto";

export const CAMPAIGN_SETTINGS_CATALOGUE: readonly ProductImageEntry[] = [
  // ── Katalog: insatser, kompetenser, taggar, roller ──────────────────────
  entry({
    id: "settings.inset-form",
    module: "Insats",
    opens: INSETS,
    setup: ["Öppna Insatser", "Välj Redigera på första insatsen"],
    sv: {
      alt: "Dialogen för en insats med namn, kategori, rörlighet och prioritet",
      shows:
        "Dialogen där en insatstyp beskrivs: namn, kategori, om besöket är omsorg eller service, hur rörligt det är och vilken prioritet det har.",
      proves: `Insatstypens regler bestäms en gång i katalogen och följer med till varje besök av den typen. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The visit-type dialog with name, category, movability and priority",
      shows:
        "The dialog where a visit type is described: name, category, whether it is care or service, how movable it is and its priority.",
      proves: `A visit type's rules are set once in the catalogue and carry over to every visit of that type. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.inset-delete",
    module: "Ta bort insats",
    opens: INSETS,
    setup: ["Öppna Insatser", "Välj Ta bort på första insatsen"],
    sv: {
      alt: "Bekräftelse innan en insats tas bort ur katalogen",
      shows:
        "Bekräftelsen som visas innan en insatstyp tas bort, med namnet på insatsen.",
      proves: `En insats försvinner aldrig ur katalogen utan att planeraren bekräftar det. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation before a visit type is removed from the catalogue",
      shows:
        "The confirmation shown before a visit type is deleted, naming the visit type.",
      proves: `A visit type never leaves the catalogue without the planner confirming it. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.inset-group-form",
    module: "Insatsgrupp",
    opens: INSETS,
    setup: ["Öppna Insatser", "Välj Ny grupp under Insatsgrupper"],
    sv: {
      alt: "Dialogen för en ny insatsgrupp med namn och minsta tid mellan besök",
      shows:
        "Dialogen där insatser samlas i en grupp med namn, beskrivning och minsta tid mellan besöken i gruppen.",
      proves: `Besök som hör ihop kan spridas över dagen med en gemensam regel i stället för en regel per kund. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The new visit-type group dialog with name and minimum time between visits",
      shows:
        "The dialog where visit types are grouped with a name, a description and a minimum time between the group's visits.",
      proves: `Related visits can be spread over the day with one shared rule instead of one rule per client. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.skill-form",
    module: "Kompetens",
    opens: SKILLS,
    setup: [
      "Öppna Kompetenser & taggar",
      "Välj Redigera på första kompetensen",
    ],
    sv: {
      alt: "Dialogen Redigera kompetens med namn, kategori och beskrivning",
      shows:
        "Dialogen där en kompetens får namn, kategori och beskrivning, till exempel ett språk eller en medicinsk behörighet.",
      proves: `Kompetenserna i katalogen är samma som matchas mellan besök och personal i planeringen. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Edit competency dialog with name, category and description",
      shows:
        "The dialog where a competency gets a name, a category and a description, such as a language or a medical qualification.",
      proves: `The competencies in the catalogue are the ones matched between visits and staff in planning. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.skill-delete",
    module: "Ta bort kompetens",
    opens: SKILLS,
    setup: ["Öppna Kompetenser & taggar", "Välj Ta bort på första kompetensen"],
    sv: {
      alt: "Frågan Ta bort kompetens? innan en kompetens tas bort",
      shows: "Bekräftelsen som visas innan en kompetens tas bort ur katalogen.",
      proves: `En kompetens tas bara bort efter en uttrycklig bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Delete competency? question before a competency is removed",
      shows:
        "The confirmation shown before a competency is removed from the catalogue.",
      proves: `A competency is removed only after an explicit confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.tag-form",
    module: "Tagg",
    opens: SKILLS,
    setup: ["Öppna Kompetenser & taggar", "Välj Redigera på första taggen"],
    sv: {
      alt: "Dialogen Redigera tagg med namn, kategori och färg",
      shows: "Dialogen där en tagg får namn, kategori och färg.",
      proves: `Taggar märker kunder och personal med egna kategorier som syns i listor och scheman. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Edit tag dialog with name, category and colour",
      shows: "The dialog where a tag gets a name, a category and a colour.",
      proves: `Tags mark clients and staff with the organisation's own categories, visible in lists and schedules. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.tag-delete",
    module: "Ta bort tagg",
    opens: SKILLS,
    setup: ["Öppna Kompetenser & taggar", "Välj Ta bort på första taggen"],
    sv: {
      alt: "Frågan Ta bort tagg? innan en tagg tas bort",
      shows: "Bekräftelsen som visas innan en tagg tas bort.",
      proves: `En tagg tas bara bort efter en uttrycklig bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Delete tag? question before a tag is removed",
      shows: "The confirmation shown before a tag is removed.",
      proves: `A tag is removed only after an explicit confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.employee-role-form",
    module: "Medarbetarroll",
    opens: ROLES,
    setup: ["Öppna Medarbetarroller", "Välj Redigera på första rollen"],
    sv: {
      alt: "Dialogen Redigera medarbetarroll med namn och operativ roll",
      shows:
        "Dialogen där en medarbetarroll får visningsnamn, beskrivning och den operativa roll den motsvarar i planeringen.",
      proves: `Verksamhetens egna rollnamn kopplas till en roll som planeringen förstår. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Edit employee role dialog with name and operational role",
      shows:
        "The dialog where an employee role gets a display name, a description and the operational role it maps to in planning.",
      proves: `The organisation's own role names are mapped to a role planning understands. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.employee-role-delete",
    module: "Ta bort medarbetarroll",
    opens: ROLES,
    setup: ["Öppna Medarbetarroller", "Välj Ta bort på första rollen"],
    sv: {
      alt: "Bekräftelse innan en medarbetarroll tas bort",
      shows: "Bekräftelsen som visas innan en medarbetarroll tas bort.",
      proves: `En roll tas bara bort efter en uttrycklig bekräftelse. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "Confirmation before an employee role is removed",
      shows: "The confirmation shown before an employee role is deleted.",
      proves: `A role is removed only after an explicit confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  // ── Användare och supportåtkomst ─────────────────────────────────────────
  entry({
    id: "settings.members",
    module: "Användare",
    opens: "Inställningar → Avancerat → Användare",
    setup: ["Öppna Inställningar", "Välj Användare under Avancerat"],
    sv: {
      alt: "Användare i organisationen med roller och inbjudningar",
      shows:
        "Organisationens användare med sina roller, där en administratör bjuder in nya och ändrar roller.",
      proves:
        "Vem som har tillgång till Caire och med vilken roll hanteras på ett ställe i organisationens inställningar.",
    },
    en: {
      alt: "Users in the organisation with roles and invitations",
      shows:
        "The organisation's users and their roles, where an administrator invites new users and changes roles.",
      proves:
        "Who has access to Caire, and with which role, is managed in one place in the organisation settings.",
    },
  }),
  entry({
    id: "settings.support-access",
    module: "Supportåtkomst",
    opens: "Inställningar → Avancerat → Supportåtkomst",
    setup: [
      "Caires support öppnar en tidsbegränsad session med ett angivet skäl",
      "Öppna Supportåtkomst",
    ],
    sv: {
      alt: "Supportåtkomst från Caire med vem, skäl, omfattning och tid",
      shows:
        "Listan över Caires supportsessioner i organisationen: vem som har åtkomst, varför, med vilken omfattning och hur länge.",
      proves:
        "Organisationen ser när Caires support har åtkomst och kan avsluta sessionen själv.",
    },
    en: {
      alt: "Caire support access with who, reason, scope and time",
      shows:
        "The list of Caire support sessions in the organisation: who has access, why, with which scope and for how long.",
      proves:
        "The organisation sees when Caire support has access and can end the session itself.",
    },
  }),
  entry({
    id: "settings.support-access-banner",
    module: "Supportåtkomst",
    opens: "Översikt med en aktiv supportsession",
    setup: [
      "Caires support öppnar en tidsbegränsad session med ett angivet skäl",
      "Öppna Översikt",
    ],
    sv: {
      alt: "Fältet överst som visar att Caires support har tillfällig åtkomst",
      shows:
        "Fältet överst i appen som visar vem från Caire som har tillfällig åtkomst och varför, med en länk för att visa och avsluta.",
      proves:
        "Så länge supporten har åtkomst syns det på varje sida, och fältet går inte att stänga bort.",
    },
    en: {
      alt: "The bar at the top showing that Caire support has temporary access",
      shows:
        "The bar at the top of the app showing who from Caire has temporary access and why, with a link to view and end it.",
      proves:
        "While support has access it is visible on every page, and the bar cannot be dismissed.",
    },
  }),
  // ── Integrationer ────────────────────────────────────────────────────────
  entry({
    id: "settings.integrations",
    module: "Integrationer",
    opens: INTEGRATIONS,
    setup: ["Öppna Inställningar", "Välj Integrationer under Avancerat"],
    sv: {
      alt: "Integrationer med CAIRE Connect, Partner-API och externa kopplingar",
      shows:
        "Sidan Integrationer där organisationens kopplingar till andra system samlas: CAIRE Connect, inbäddade vyer, Partner-API och webhookar.",
      proves:
        "Alla kopplingar till andra system syns och styrs från en sida i organisationens inställningar.",
    },
    en: {
      alt: "Integrations with CAIRE Connect, Partner API and external connections",
      shows:
        "The Integrations page that gathers the organisation's connections to other systems: CAIRE Connect, embedded views, the Partner API and webhooks.",
      proves:
        "Every connection to another system is visible and controlled from one page in the organisation settings.",
    },
  }),
  entry({
    id: "settings.domain-authority",
    module: "Auktoritativ datakälla",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Gå till Auktoritativ datakälla"],
    sv: {
      alt: "Auktoritativ datakälla per datadomän",
      shows:
        "Panelen där organisationen anger om CAIRE eller en godkänd partner är källan för varje datadomän.",
      proves:
        "Det är uttalat vilket system som äger kunder, personal och besök, så att två system inte skriver över varandra.",
    },
    en: {
      alt: "Data source authority per data domain",
      shows:
        "The panel where the organisation declares whether CAIRE or an admitted partner is the source for each data domain.",
      proves:
        "It is explicit which system owns clients, staff and visits, so two systems do not overwrite each other.",
    },
  }),
  entry({
    id: "settings.connect-overview",
    module: "CAIRE Connect",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Gå till CAIRE Connect – översikt"],
    sv: {
      alt: "CAIRE Connect – översikt med kopplingar och krav",
      shows:
        "Översikten över CAIRE Connect: antal kopplingar och nycklar, vilka partner som är anslutna och vilka krav som är uppfyllda.",
      proves:
        "Organisationen ser vilka partner som är godkända och vad som saknas innan en koppling kan användas.",
    },
    en: {
      alt: "CAIRE Connect overview with connections and requirements",
      shows:
        "The CAIRE Connect overview: number of connections and keys, which partners are connected and which requirements are met.",
      proves:
        "The organisation sees which partners are admitted and what is missing before a connection can be used.",
    },
  }),
  entry({
    id: "settings.connect-embed-settings",
    module: "Inbäddade vyer",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Gå till Inbäddade vyer"],
    sv: {
      alt: "Inbäddade vyer med val per partnerkoppling",
      shows:
        "Panelen där organisationen väljer vilka skrivskyddade analys- och planvyer en godkänd partner får öppna.",
      proves:
        "En partner kan bara visa de vyer organisationen har slagit på, och valet ger ingen dataåtkomst.",
    },
    en: {
      alt: "Embedded views with a choice per partner connection",
      shows:
        "The panel where the organisation chooses which read-only analysis and plan views an admitted partner may open.",
      proves:
        "A partner can show only the views the organisation has switched on, and the choice grants no data access.",
    },
  }),
  entry({
    id: "settings.partner-api-clients",
    module: "Partner-API",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Gå till Partner-API"],
    sv: {
      alt: "Partner-API med tokenadress och nycklar per system",
      shows:
        "Panelen Partner-API med tokenadress, API-beskrivning och organisationens nycklar med åtkomst, enheter och senaste användning.",
      proves:
        "Varje system som hämtar data får en egen nyckel som bara gäller de enheter och den åtkomst som valts.",
    },
    en: {
      alt: "Partner API with token URL and one key per system",
      shows:
        "The Partner API panel with token URL, API description and the organisation's keys with access, units and last use.",
      proves:
        "Each system that reads data gets its own key, limited to the units and access chosen.",
    },
  }),
  entry({
    id: "settings.partner-webhooks",
    module: "Webhookar",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Gå till Webhookar"],
    sv: {
      alt: "Webhookar med adress, nyckel, händelser och leveranslogg",
      shows:
        "Panelen Webhookar med mottagaradress, vilken nyckel den hör till, vilka händelser den får och leveransloggen.",
      proves:
        "CAIRE skickar signerade händelser till partnern och varje adress får bara de händelser nyckelns åtkomst täcker.",
    },
    en: {
      alt: "Webhooks with address, key, events and delivery log",
      shows:
        "The Webhooks panel with receiver address, the key it belongs to, the events it receives and the delivery log.",
      proves:
        "CAIRE sends signed events to the partner, and each address receives only the events its key's access covers.",
    },
  }),
  entry({
    id: "settings.partner-api-create-client",
    module: "Ny nyckel",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Välj Skapa nyckel under Partner-API"],
    sv: {
      alt: "Panelen för en ny API-nyckel med namn, åtkomst och enheter",
      shows:
        "Panelen där en ny nyckel får namn, åtkomstnivå och de enheter den gäller.",
      proves: `Åtkomsten väljs innan nyckeln finns, och känsliga uppgifter kräver ett eget godkännande. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The new API key panel with name, access and units",
      shows:
        "The panel where a new key gets a name, an access level and the units it covers.",
      proves: `Access is chosen before the key exists, and sensitive data needs its own confirmation. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.partner-api-edit-client",
    module: "Ändra nyckel",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Välj Ändra nyckel på en nyckel"],
    sv: {
      alt: "Panelen Ändra nyckel med åtkomst och enheter för en befintlig nyckel",
      shows:
        "Panelen där en befintlig nyckels namn, åtkomst och enheter ändras.",
      proves: `En nyckels åtkomst kan snävas in eller utökas utan att systemet behöver en ny nyckel. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The Edit key panel with access and units for an existing key",
      shows:
        "The panel where an existing key's name, access and units are changed.",
      proves: `A key's access can be narrowed or widened without the system needing a new key. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.partner-api-secret",
    module: "Nyckel och hemlighet",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Välj Ny hemlighet på en testnyckel"],
    sv: {
      alt: "Dialogen med klient-id, tokenadress och en hemlighet som visas en gång",
      shows:
        "Dialogen som visar klient-id, tokenadress och hemligheten för en nyckel, med varningen att hemligheten bara visas nu.",
      proves: `CAIRE sparar inte hemligheten och kan inte visa den igen. ${MASKED_SV}`,
    },
    en: {
      alt: "The dialog with client ID, token URL and a secret shown once",
      shows:
        "The dialog showing a key's client ID, token URL and secret, with the warning that the secret is shown only now.",
      proves: `CAIRE does not store the secret and cannot show it again. ${MASKED_EN}`,
    },
  }),
  entry({
    id: "settings.partner-webhook-endpoint",
    module: "Webhook",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Välj Ändra på en webhook"],
    sv: {
      alt: "Panelen för en webhook med nyckel, adress och händelser",
      shows:
        "Panelen där en webhook får mottagaradress, nyckel och de händelser den ska ta emot.",
      proves: `Bara händelser som nyckelns åtkomst täcker går att välja. ${NOT_SAVED_SV}`,
    },
    en: {
      alt: "The webhook panel with key, address and events",
      shows:
        "The panel where a webhook gets a receiver address, a key and the events it should receive.",
      proves: `Only events covered by the key's access can be chosen. ${NOT_SAVED_EN}`,
    },
  }),
  entry({
    id: "settings.partner-webhook-secret",
    module: "Signeringsnyckel",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Välj Ny signeringsnyckel på en webhook"],
    sv: {
      alt: "Dialogen Signeringsnyckel med adress och en nyckel som visas en gång",
      shows:
        "Dialogen med webhookens adress och den nya signeringsnyckeln som mottagaren använder för att kontrollera händelserna.",
      proves: `Den tidigare nyckeln fortsätter signera i 24 timmar så att mottagaren hinner byta. ${MASKED_SV}`,
    },
    en: {
      alt: "The Signing secret dialog with address and a secret shown once",
      shows:
        "The dialog with the webhook address and the new signing secret the receiver uses to verify events.",
      proves: `The previous secret keeps signing for 24 hours so the receiver has time to switch. ${MASKED_EN}`,
    },
  }),
  entry({
    id: "settings.external-connectors",
    module: "Externa schemakopplingar",
    opens: INTEGRATIONS,
    setup: ["Öppna Integrationer", "Gå till Externa schemakopplingar"],
    sv: {
      alt: "Externa schemakopplingar med åtkomstuppgifter och val av enhet",
      shows:
        "Panelen där en koppling till ett externt schemasystem konfigureras och övervakas, en enhet i taget.",
      proves:
        "Schemadata från ett annat system läses in per enhet, och kopplingen kan pausas och följas upp här.",
    },
    en: {
      alt: "External schedule connections with credentials and unit choice",
      shows:
        "The panel where a connection to an external scheduling system is configured and monitored, one unit at a time.",
      proves:
        "Schedule data from another system is read per unit, and the connection can be paused and followed up here.",
    },
  }),
  entry({
    id: "settings.connect-launch",
    module: "CAIRE Connect",
    opens: "/connect/launch utan giltig startlänk",
    setup: ["Öppna startsidan för CAIRE Connect utan en länk från partnern"],
    sv: {
      alt: "Startsidan för CAIRE Connect när startlänken inte går att använda",
      shows:
        "Sidan som öppnar en skrivskyddad CAIRE-vy från ett partnersystem, här när länken saknas eller redan har använts.",
      proves:
        "En vy från en partner öppnas bara med en giltig engångslänk; annars förklarar sidan vad användaren ska göra.",
    },
    en: {
      alt: "The CAIRE Connect launch page when the launch link cannot be used",
      shows:
        "The page that opens a read-only CAIRE view from a partner system, here when the link is missing or already used.",
      proves:
        "A partner view opens only with a valid one-time link; otherwise the page explains what to do.",
    },
  }),
  entry({
    id: "settings.connect-embed-unavailable",
    module: "CAIRE Connect",
    opens: "Inbäddad planvy utan partnerstart",
    setup: ["Öppna en inbäddad planvy utan att komma från en partner"],
    sv: {
      alt: "Meddelandet Vyn är inte tillgänglig i en inbäddad vy",
      shows:
        "Meddelandet som visas när en inbäddad analys- eller planvy öppnas utan en giltig start från partnern.",
      proves:
        "Inbäddade vyer visar ingen data utan en godkänd start, och användaren får en väg vidare in i CAIRE.",
    },
    en: {
      alt: "The view is unavailable message in an embedded view",
      shows:
        "The message shown when an embedded analysis or plan view is opened without a valid start from the partner.",
      proves:
        "Embedded views show no data without an admitted start, and the user gets a way into CAIRE.",
    },
  }),
  // ── Konto och produkter ──────────────────────────────────────────────────
  entry({
    id: "settings.account.billing",
    module: "Fakturering & Prenumeration",
    opens: `${ACCOUNT} → Fakturering & Prenumeration`,
    setup: ["Öppna Konto", "Välj Fakturering & Prenumeration"],
    area: "operations",
    sv: {
      alt: "Fakturering & Prenumeration med provperiod, produkter och betalning",
      shows:
        "Fliken där organisationen ser sin provperiod, sina produkter och platser och öppnar betalningsportalen.",
      proves:
        "Prenumeration och betalning sköts av organisationens administratör direkt från kontot.",
    },
    en: {
      alt: "Billing & Subscription with trial, products and payment",
      shows:
        "The tab where the organisation sees its trial, products and seats and opens the payment portal.",
      proves:
        "Subscription and payment are handled by the organisation's administrator directly from the account.",
    },
  }),
  entry({
    id: "settings.mittcaire-product",
    module: "Mitt Caire",
    opens: "Konto → Produkter → Mitt Caire",
    setup: ["Öppna produktsidan för Mitt Caire"],
    area: "operations",
    sv: {
      alt: "Produktsidan för Mitt Caire med innehåll och aktivering",
      shows:
        "Produktsidan för Mitt Caire med vad som ingår och hur organisationen aktiverar den.",
      proves:
        "Organisationen ser vad Mitt Caire innehåller och aktiverar den från samma konto som övriga produkter.",
    },
    en: {
      alt: "The Mitt Caire product page with contents and activation",
      shows:
        "The Mitt Caire product page with what is included and how the organisation activates it.",
      proves:
        "The organisation sees what Mitt Caire contains and activates it from the same account as its other products.",
    },
  }),
  entry({
    id: "settings.mittcaire-activation-return",
    module: "Mitt Caire",
    opens: "/activate-mittcaire utan returlänk",
    setup: ["Öppna aktiveringen av Mitt Caire utan en returlänk"],
    area: "operations",
    sv: {
      alt: "Sidan Ansluter till Mitt Caire när returlänken saknas",
      shows:
        "Sidan som verifierar organisationen innan användaren skickas tillbaka till Mitt Caire, här när returlänken saknas.",
      proves:
        "Användaren skickas bara vidare till en godkänd returadress; annars visas en väg tillbaka till produkterna.",
    },
    en: {
      alt: "The Connecting to Mitt Caire page when the return link is missing",
      shows:
        "The page that verifies the organisation before sending the user back to Mitt Caire, here when the return link is missing.",
      proves:
        "Users are sent on only to an approved return address; otherwise the page offers a way back to products.",
    },
  }),
  entry({
    id: "settings.mobile-product-manage",
    module: "Mobilapp personal",
    opens: "Konto → Produkter → Hantera Mobilapp personal",
    setup: ["Öppna Hantera Mobilapp personal"],
    area: "operations",
    sv: {
      alt: "Hantera Mobilapp personal med mottagare och status för inbjudan",
      shows:
        "Sidan där personalens e-postadresser för appen hanteras och där inbjudningar skickas, skickas igen eller återkallas.",
      proves:
        "Administratören ser vem som har appen, vem som är inbjuden och vem som saknar inbjudan.",
    },
    en: {
      alt: "Manage the Employee Mobile App with recipients and invitation status",
      shows:
        "The page where staff app email addresses are managed and invitations are sent, re-sent or revoked.",
      proves:
        "The administrator sees who has the app, who is invited and who has no invitation yet.",
    },
  }),
  entry({
    id: "settings.mobile-recipient-confirmation",
    module: "Skicka appinbjudan",
    opens: "Konto → Produkter → Hantera Mobilapp personal",
    setup: [
      "Öppna Hantera Mobilapp personal",
      "Välj Skicka inbjudan för en medarbetare",
    ],
    area: "operations",
    sv: {
      alt: "Frågan Skicka appinbjudan? med mottagarens e-postadress",
      shows:
        "Bekräftelsen som visar vilken adress inbjudan går till innan den skickas.",
      proves:
        "Ingen inbjudan skickas utan att administratören har sett och bekräftat mottagaren. Dialogen stängs utan att något skickas.",
    },
    en: {
      alt: "The Send App Invitation? question with the recipient's email",
      shows:
        "The confirmation that shows which address the invitation goes to before it is sent.",
      proves:
        "No invitation is sent until the administrator has seen and confirmed the recipient. The dialog is closed without sending anything.",
    },
  }),
  entry({
    id: "settings.user-profile",
    module: "Min profil",
    opens: "/user-profile",
    setup: ["Öppna din profil från användarmenyn"],
    area: "operations",
    sv: {
      alt: "Användarprofilen med kontouppgifter och säkerhet",
      shows:
        "Den inloggade användarens profil med namn, e-post, inloggningsmetoder och aktiva sessioner.",
      proves:
        "Varje användare sköter sina egna inloggningsuppgifter och ser var kontot är inloggat.",
    },
    en: {
      alt: "The user profile with account details and security",
      shows:
        "The signed-in user's profile with name, email, sign-in methods and active sessions.",
      proves:
        "Each user manages their own sign-in details and sees where the account is signed in.",
    },
  }),
  // ── Kommunikation och hjälp ──────────────────────────────────────────────
  entry({
    id: "communication.help-center.mandatory-actions",
    module: "Hjälp",
    opens: "Hjälp → Komma igång",
    setup: ["Öppna Hjälp", "Välj Komma igång", "Öppna det första steget"],
    area: "operations",
    sv: {
      alt: "Ett obligatoriskt steg i Komma igång med status och genvägar",
      shows:
        "Det öppnade steget i Komma igång med vad som är klart, vad som saknas och länkar till rätt ställe i produkten och i guiderna.",
      proves:
        "Planeraren ser vilka uppgifter som behövs för det första schemat och kommer direkt dit de fylls i.",
    },
    en: {
      alt: "A required step in Get started with status and shortcuts",
      shows:
        "The opened Get started step with what is done, what is missing and links to the right place in the product and the guides.",
      proves:
        "The planner sees which details the first schedule needs and goes straight to where they are entered.",
    },
  }),
  entry({
    id: "communication.contextual-help",
    module: "Hjälp i sidan",
    opens: "Resurser → Kunder → kund → Redigera → Hjälp",
    setup: ["Öppna en kund och välj Redigera", "Välj Hjälp i sidhuvudet"],
    area: "operations",
    sv: {
      alt: "Hjälprutan för det här fältet med rekommendation och användning",
      shows:
        "Hjälprutan som öppnas från sidhuvudet: vad fältet betyder, hur det används i schemat och i resultatet, och en länk till sidopanelen.",
      proves:
        "Hjälpen utgår från sidan planeraren står på i stället för en allmän manual.",
    },
    en: {
      alt: "The Help for this field box with recommendation and usage",
      shows:
        "The help box opened from the page header: what the field means, how it is used in the schedule and in the result, and a link to the side panel.",
      proves:
        "Help starts from the page the planner is on instead of a general manual.",
    },
  }),
  entry({
    id: "communication.mobile-app-invite.signed-in",
    module: "Appinbjudan",
    opens: "/mobile-app-invite som inloggad",
    setup: ["Öppna en appinbjudan i en webbläsare där du redan är inloggad"],
    area: "operations",
    sv: {
      alt: "Appinbjudan med meddelandet Du är redan inloggad",
      shows:
        "Sidan för appinbjudan när någon redan är inloggad i webbläsaren, med en knapp för att logga ut först.",
      proves:
        "Inbjudan kopplas till rätt mobilkonto eftersom den inte går att ta emot med ett annat inloggat konto.",
    },
    en: {
      alt: "The app invitation with the You are already signed in message",
      shows:
        "The app invitation page when someone is already signed in in the browser, with a button to sign out first.",
      proves:
        "The invitation is linked to the right mobile account because it cannot be accepted with another signed-in account.",
    },
  }),
  entry({
    id: "communication.open-shift-link",
    module: "Öppet pass",
    opens: "/oppet-pass/:token",
    setup: ["Öppna länken i ett sms om ett öppet pass"],
    area: "operations",
    sv: {
      alt: "Sidan Ett öppet pass väntar på dig med länk till appen",
      shows:
        "Sidan som sms-länken om ett öppet pass leder till, med knappar för att öppna eller installera appen.",
      proves:
        "Länken visar inga uppgifter om pass, kunder eller person; passet visas och besvaras i appen.",
    },
    en: {
      alt: "The An open shift is waiting for you page with a link to the app",
      shows:
        "The page an open-shift text message links to, with buttons to open or install the app.",
      proves:
        "The link shows nothing about the shift, clients or person; the shift is shown and answered in the app.",
    },
  }),
];
